import React from 'react';
import _ from 'lodash';
import log from '@wix/wix-log';
import type { NextPage } from 'next';
import Head from 'next/head';
import {
  HydrationBoundary,
  QueryClient,
  dehydrate,
} from '@tanstack/react-query';
import { web } from '@wix/wix-common-aspects';
import { WixDesignSystemProvider } from '@wix/design-system';
import { Service } from '@wix/ambassador-marketplace-categories-v1-category/types';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  IParams,
  Filters,
  SITE_TYPES,
  INDUSTRIES,
  FEATURES,
  STYLES,
  PRICES,
  SortingMethod,
  SEOProps,
} from '@common/types';
import { getCleanUrl } from '@utils/urlUtils';
import { isValidSortingMethod } from '@utils/typeUtils';
import { TemplatesContextProvider } from '@components/Contexts/TemplatesContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import Templates from '@components/Templates';
import { queryKeyFrom } from '@utils/queryUtils';
import createTemplatesSeoAPI from '@services/TemplatesSeoAPI';
import createInitialStateService from '@services/InitialStateService';
import { getWixContext } from '@utils/async-store';
import { ENGLISH_LANGUAGE_CODE } from '@utils/constants';
import { getPageLanguage } from '@app/utils';
import { QueryTemplatesParams } from '@services/TemplateListingAPI';

const TemplatesPage: NextPage<{
  services: Service[];
  categoriesForHeader: Array<Service> | undefined;
  dehydratedState: any;
  numberOfResults: number;
  numberOfPages: number;
  isMobileUserAgent: boolean;
  origin: string;
  entry: any;
  host: string;
  seo: SEOProps;
  filters: Filters;
  currentPage: number;
  sort: SortingMethod;
  userCurrency: string;
  currencyConversionRate: number;
  taxRate: number;
  isInPriceTaxInclusiveCountry: boolean;
  error?: any;
}> = ({
  isMobileUserAgent,
  dehydratedState,
  numberOfResults,
  numberOfPages,
  origin,
  entry,
  host,
  services,
  categoriesForHeader,
  seo: { title, metaTags, links },
  filters,
  currentPage,
  sort,
  userCurrency,
  currencyConversionRate,
  taxRate,
  isInPriceTaxInclusiveCountry,
  error,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const TemplatesHeader = () => {
    const pageTitle =
      currentPage > 1 ? `${title} - Page ${currentPage}` : title;

    return (
      <Head>
        <title>{pageTitle}</title>
        {_.map(links, (attr, index) =>
          attr.rel === 'canonical' ? null : (
            <link
              {...(attr.hrefLang && { hrefLang: attr.hrefLang })}
              rel={attr.rel}
              href={attr.href}
              key={`lang-${index}`}
            />
          ),
        )}
        {_.map(metaTags, (attr, index) => (
          <meta {...attr} key={`meta-${index}`} />
        ))}
        <link
          rel="canonical"
          href={_.get(_.find(links, { rel: 'canonical' }), 'href')}
        ></link>
      </Head>
    );
  };

  return (
    <>
      <TemplatesHeader />
      <BiSessionContextProvider>
        <CurrencyContextProvider
          currencyData={{
            currencyCode: userCurrency,
            conversionRate: currencyConversionRate,
          }}
          taxRate={taxRate}
          isInPriceTaxInclusiveCountry={isInPriceTaxInclusiveCountry}
        >
          <HeaderContextProvider
            services={categoriesForHeader || services}
            host={host}
            origin={origin}
            entry={entry}
          >
            <HydrationBoundary state={dehydratedState}>
              <TemplatesContextProvider
                numberOfResults={numberOfResults}
                numberOfPages={numberOfPages}
                isMobileUserAgent={isMobileUserAgent}
                origin={origin}
                entry={entry}
                host={host}
                filters={filters}
                currentPage={currentPage}
                sort={sort}
                error={error}
              >
                <WixDesignSystemProvider features={{ newColorsBranding: true }}>
                  <Templates />
                </WixDesignSystemProvider>
              </TemplatesContextProvider>
            </HydrationBoundary>
          </HeaderContextProvider>
        </CurrencyContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

function getSiteTypeFilterFromUrl(query: IParams) {
  const siteTypeFromUrl = query.siteType as string;
  const siteType =
    SITE_TYPES[_.toUpper(siteTypeFromUrl) as keyof typeof SITE_TYPES] ??
    undefined;
  return siteType === 'ALL' ? undefined : siteType;
}

function getSearchFilterFromUrl(query: IParams) {
  const searchFromUrl = query.search;
  if (typeof searchFromUrl !== 'string') {
    return null;
  }
  const searchTerm = searchFromUrl.trim();
  return searchTerm;
}

function getIndustryFilterFromUrl(query: IParams) {
  const industriesFromUrl = query.industries as string;
  const industries = _.isUndefined(industriesFromUrl)
    ? null
    : industriesFromUrl.split(',');

  return _.reduce(
    industries,
    (acc: string[], industry) => {
      if (INDUSTRIES[_.toUpper(industry) as keyof typeof INDUSTRIES]) {
        acc.push(industry);
      }
      return acc;
    },
    [],
  );
}

function getFeaturesFilterFromUrl(query: IParams) {
  const featuresFromUrl = query.features as string;
  const features = _.isUndefined(featuresFromUrl)
    ? null
    : featuresFromUrl.split(',');

  return _.reduce(
    features,
    (acc: string[], feature) => {
      if (FEATURES[_.toUpper(feature) as keyof typeof FEATURES]) {
        acc.push(feature);
      }
      return acc;
    },
    [],
  );
}

function getMultiLanguagesFilterFromUrl(
  query: IParams,
  supportedLanguages: any,
) {
  const langsFromUrl = query.languages as string;
  const langs = _.isUndefined(langsFromUrl) ? null : langsFromUrl.split(',');

  return _.reduce(
    langs,
    (acc: string[], lang) => {
      const languageObj = _.get(_.find(supportedLanguages, { id: lang }), 'id');
      acc.push(languageObj);
      return acc;
    },
    [],
  );
}

function getPriceParamsFilterFromUrl(query: IParams): any {
  const priceFromUrl = query.price as string;
  const price =
    PRICES[_.toUpper(priceFromUrl) as keyof typeof PRICES] ?? undefined;
  return price === 'ALL' ? undefined : price;
}

function getStyleFilterFromUrl(query: IParams) {
  const stylesFromUrl = query.styles as string;
  const styles = _.isUndefined(stylesFromUrl) ? null : stylesFromUrl.split(',');

  return _.reduce(
    styles,
    (acc: string[], style) => {
      if (STYLES[_.toUpper(style) as keyof typeof STYLES]) {
        acc.push(style);
      }
      return acc;
    },
    [],
  );
}

function getSortFromUrl(query: IParams): any {
  const sortFromUrl = query.sort as string;
  return isValidSortingMethod(sortFromUrl) ? sortFromUrl : 'relevance';
}

export async function getServerSideProps({
  req,
  res,
  query,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
}) {
  const wixContext = getWixContext();
  const initialStateService = createInitialStateService(req.aspects, res);
  const language = getPageLanguage({
    aspects: req.aspects,
    pageName: 'templates',
  });
  const url = req.aspects?.get(web.Url);
  const servicesAPI = wixContext.serviceFactory.servicesAPI();
  const languagesAPI = wixContext.serviceFactory.languagesAPI();
  const templateListingAPI = wixContext.serviceFactory.templateListingAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();

  const { entry, origin } = query;
  const page = query.page as string;
  const serviceIds = query.serviceIds as string;
  try {
    const [allServicesWithHierarchy, categoriesForHeader, languages] =
      await Promise.all([
        servicesAPI.getAllServicesWithHierarchy({
          withPathName: true,
          withTranslatedKey: true,
          language,
        }),
        servicesAPI.getCategoriesForHeader({
          withPathName: true,
          withTranslatedKey: true,
          language,
        }),
        languagesAPI.getLanguages(),
      ]);

    const selectedSiteType = getSiteTypeFilterFromUrl(query);
    const industries = getIndustryFilterFromUrl(query);
    const features = getFeaturesFilterFromUrl(query);
    const selectedLanguages = getMultiLanguagesFilterFromUrl(query, languages);
    const styles = getStyleFilterFromUrl(query);
    const selectedPrice = getPriceParamsFilterFromUrl(query);
    const currentPage = _.toNumber(page) || 1;
    const sort = getSortFromUrl(query);
    const search = getSearchFilterFromUrl(query);

    const siteType = selectedSiteType || 'ALL';
    const price = selectedPrice || 'ALL';

    const shouldTranslateSearchTerm = language !== ENGLISH_LANGUAGE_CODE;
    const filters = {
      languages: selectedLanguages,
      features,
      industries,
      siteType,
      styles,
      price,
      search,
    } as QueryTemplatesParams;

    const queryClient = new QueryClient();
    const createQueryKey = ({ isInfinite = false }) =>
      queryKeyFrom(filters, isInfinite ? 1 : currentPage, sort, isInfinite);

    const createQueryFn =
      ({ isInfinite = false }) =>
      async () => {
        const { templates, numberOfPages, count, hasNextPage } =
          await templateListingAPI.queryTemplates({
            ...filters,
            page: isInfinite ? 1 : currentPage,
            sort,
            shouldTranslateSearchTerm,
          });
        return {
          templates,
          numberOfPages,
          count,
          hasNextPage,
        };
      };

    const templatesSeoAPI = createTemplatesSeoAPI(
      res.locals.appContext,
      req.aspects,
    );

    await Promise.all([
      queryClient.prefetchInfiniteQuery({
        queryKey: createQueryKey({ isInfinite: true }),
        queryFn: createQueryFn({ isInfinite: true }),
        initialPageParam: 1,
      }),
      queryClient.prefetchQuery({
        queryKey: createQueryKey({ isInfinite: false }),
        queryFn: createQueryFn({ isInfinite: false }),
      }),
    ]);

    const {
      userCurrency,
      currencyConversionRate,
      countryTaxRate,
      isInPriceTaxInclusiveCountry,
    } = await currencyAPI.getGeoBasedTaxAndCurrencyRate({
      aspects: req.aspects,
      loggerName: 'templates',
    });

    const cleanUrl = getCleanUrl(allServicesWithHierarchy, url, serviceIds);

    const { numberOfPages } = await templateListingAPI.queryTemplates({
      ...filters,
      page: currentPage,
      sort,
      shouldTranslateSearchTerm,
    });

    const [title, metaTags, links] = await Promise.all([
      templatesSeoAPI.getPageTitle(language),
      templatesSeoAPI.getPageMetaTags(cleanUrl, language),
      templatesSeoAPI.getPageLinkTags(cleanUrl, page, numberOfPages),
    ]);

    const props = {
      services: allServicesWithHierarchy,
      categoriesForHeader,
      host: `https://${req.get('host')}`,
      seo: {
        title,
        metaTags,
        links,
      },
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      ...(origin && { origin }),
      ...(entry && { entry }),
      ...(filters && { filters }),
      currentPage,
      ...(sort && { sort }),
      userCurrency,
      currencyConversionRate,
      taxRate: countryTaxRate,
      isInPriceTaxInclusiveCountry,
      templatesAverageScore: 0,
    };
    const initialState = await initialStateService.create();
    return {
      props: {
        initialState,
        ...props,
      },
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('templates')
      .withAspects(req.aspects)
      .error(`error w/ templates`, { message, stack });
    const initialState = await initialStateService.create();
    return {
      props: {
        initialState,
        error: message,
        seo: {
          title: `Wix Templates Marketplace`,
        },
        templatesAverageScore: 0,
      },
    };
  }
}

export default TemplatesPage;
